import { spaces } from '@kimuno/theme';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { campaign, onboardingHints, routes } from '../../constants';
import { selectUser } from '../../reducers/user.selectors';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../services/track.service';
import { shouldDisplayHintsAndOnboardingDialogs } from '../../utils/shouldDisplayHintsAndOnboardingDialogs';
import useOnboardingDialog from '../../utils/useOnboardingDialog';
import useRouter from '../../utils/useRouter';
import AlertDialog from '../Dialog/AlertDialog';

const AiOnlyWelcomeDialog = () => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);

	const { isShown, markDialogAsSeen } = useOnboardingDialog(onboardingHints.aiOnlyWelcomeDialog);

	const { match } = useRouter();
	const currentPath = match.location.pathname;
	const isAllowedPage = useMemo(
		() => currentPath.startsWith(`/${routes.aiTools.root}`),
		[currentPath]
	);

	const shouldShowDialog = useMemo(() => {
		if (!user?.isAiOnlyUser) return false;
		if (!shouldDisplayHintsAndOnboardingDialogs()) return false;
		return isAllowedPage && !isShown;
	}, [user?.isAiOnlyUser, isAllowedPage, isShown]);

	const closeDialog = () => {
		ampliTrackMessageDisplayedEvent(campaign.aiOnlyWelcomeMessage, MessageType.DIALOG, 1);
		markDialogAsSeen();
	};

	if (!shouldShowDialog) return null;

	return (
		<AlertDialog
			variant="text"
			open={!isShown}
			onClose={closeDialog}
			title={`${t('Welcome to meinUnterricht KI, {{firstName}}', { firstName: user?.firstName })} 👋`}
			description={
				<>
					{t('aiOnlyWelcomeIntro')}
					<Box
						component="ul"
						sx={{
							listStyle: 'none',
							paddingLeft: spaces.sm,
							marginTop: spaces.sm,
							marginBottom: 0,
						}}
					>
						<li>{t('aiOnlyWelcomeBullet1')}</li>
						<li>{t('aiOnlyWelcomeBullet2')}</li>
						<li>{t('aiOnlyWelcomeBullet3')}</li>
					</Box>
				</>
			}
			primaryButtonText={t('Get started now')}
			onPrimaryClick={closeDialog}
		/>
	);
};

export default AiOnlyWelcomeDialog;
